import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendarEmptyHeader, ConfirmationDialogComponent, InlineDatePickerComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { TimeOffDateFormatDashes } from '@app/modules/time-off-v3/meta/time-off-meta';
import { LocalizedDatePipe } from '@app/pipes/localized-date/localized-date.pipe';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { RoutingStateService } from '@common/services/routing-state.service';
import { CompanySetting } from '@models/settings/company-setting.model';
import { TimeOffRequest, TimeOffRequestStatus } from '@models/time-off-v3/time-off-request.model';
import { TimeOffBalance } from '@time-off-v3/interfaces/time-off-balance.interface';
import { TimeOffService } from '@time-off-v3/services';
import { OverlappingRequestsTable } from '@time-off-v3/services/overlapping-requests-table/overlapping-requests.table';
import moment from 'moment';

const visibilitySetting = 'timeOffRequests.visibility';

@Component({
    templateUrl: './request-show.template.html',
    styleUrls: ['./request-show.style.scss', '../../../../../styles/shared-views/time-off-request.scss'],
})
export class RequestShowView implements OnInit {
    @ViewChild('deleteRequestModal') deleteRequestModal!: ConfirmationDialogComponent;
    @ViewChild('resetRequestModal') resetRequestModal!: ConfirmationDialogComponent;
    @ViewChild('forceApproveModal') forceApproveModal!: ConfirmationDialogComponent;
    @ViewChild('forceDenyModal') forceDenyModal!: ConfirmationDialogComponent;
    @ViewChild('calendar') calendar: InlineDatePickerComponent;
    timeOffRequest = new TimeOffRequest();
    balanceBefore: number;
    isLoading = true;
    isLoadingApprovalFlow = false;
    isUpdating = false;
    isUnableToDeleteModalOpen = false;
    isUnableToEditModalOpen = false;
    isForceDenyRequestCommentModalOpen = false;
    overlappingRequestsTable: OverlappingRequestsTable;
    canViewOverlaps = false;
    backRoute = '';
    canDeleteRequest: boolean;
    warnings: string[] = [];
    errors: string[] = [];
    hasBanners = false;
    calendarPickerHeader = CalendarEmptyHeader;
    canEdit = false;
    canReset = false;
    canForceApprove = false;
    canForceDeny = false;

    constructor(
        public router: Router,
        private route: ActivatedRoute,
        private timeOffService: TimeOffService,
        private notify: NotifyService,
        private auth: AuthService,
        private location: Location,
        private routingState: RoutingStateService,
        private analyticService: AnalyticService,
        private localizedDatePipe: LocalizedDatePipe
    ) {
        this.timeOffRequest = this.route.snapshot.data.models.get('request');

        CompanySetting.param('company', this.auth.company.id)
            .where('module', 'TimeOffv3')
            .with('setting')
            .get()
            .then(([companySettings]) => {
                const visibleTimeOff = companySettings.find(
                    (setting) => setting.setting.name === visibilitySetting
                ).value;

                this.canViewOverlaps = this.isAdmin || this.isManager || visibleTimeOff;

                if (!this.canViewOverlaps) {
                    return;
                }

                this.overlappingRequestsTable = new OverlappingRequestsTable(this.auth, this.timeOffRequest);
            });

        this.timeOffService
            .getBalance(this.timeOffRequest.employeeId, {
                'filters[processedAt][to]': moment(this.timeOffRequest.startAt)
                    .subtract(1, 'days')
                    .format('YYYY-MM-DD'),
                'filters[timeOffTypeId]': this.timeOffRequest.timeOffPolicy.timeOffTypeId,
                'filters[timeOffRequestId]': this.timeOffRequest.id,
            })
            .then(
                (timeOffBalance: TimeOffBalance) =>
                    (this.balanceBefore = this.timeOffRequest.showHourly
                        ? timeOffBalance.balanceHours
                        : timeOffBalance.balance)
            );
    }

    get startAtString(): string {
        return this.localizedDatePipe.transform(moment(this.timeOffRequest.startAt), 'short') ?? '';
    }

    get endAtString(): string {
        return this.localizedDatePipe.transform(moment(this.timeOffRequest.endAt), 'short') ?? '';
    }

    get isAdmin(): boolean {
        return this.auth.isAdmin();
    }

    get isManager(): boolean {
        return this.auth.supervises(this.timeOffRequest.employee);
    }

    get isRequester(): boolean {
        return this.auth.employee.id === this.timeOffRequest.employeeId;
    }

    get isPastApprovedRequest(): boolean {
        return (
            this.timeOffRequest?.status === TimeOffRequestStatus.approved &&
            moment(this.timeOffRequest?.startAt).isBefore(moment(), 'day')
        );
    }

    get canViewMeatballsActions(): boolean {
        return this.isAdmin || this.isManager || (this.isRequester && !this.isPastApprovedRequest);
    }

    get isForcedApproved(): boolean {
        return this.timeOffRequest.timeOffRequestApprovalFlow.isForcedApproved;
    }

    get isForcedDenied(): boolean {
        return this.timeOffRequest.timeOffRequestApprovalFlow.isForcedDenied;
    }

    async ngOnInit(): Promise<void> {
        this.setIsLoading(true);

        // Do not redirect back to the time-off create or edit page (causing back button loop)
        this.backRoute = this.routingState.firstPreviousUrlNotMatching(/(\/edit|\/create)/);

        await this.updatePermissions();
        this.setIsLoading(false);
    }

    async updatePermissions(): Promise<void> {
        this.canDeleteRequest = this.showDelete();

        // We don't need to verify rules since the request has been already approved.
        if (this.timeOffRequest.status !== TimeOffRequestStatus.approved) {
            await this.verifyBusinessRules();
        }

        this.canEdit = this.canEditCheck();
        this.canReset = this.canResetCheck();
        this.canForceApprove = this.canForceApproveCheck();
        this.canForceDeny = this.canForceDenyCheck();
    }

    async verifyBusinessRules(): Promise<void> {
        const { warnings, errors } = await this.timeOffService.verifyBusinessRules(this.timeOffRequest);

        this.warnings = warnings ?? [];
        this.errors = errors ?? [];
        this.hasBanners = !!this.warnings.length || !!this.errors.length;
    }

    async reloadTimeOffRequest(): Promise<void> {
        this.timeOffRequest = await TimeOffRequest.with([
            'employee',
            'createdBy',
            'timeOffRequestDays',
            'timeOffPolicy.timeOffType',
            'timeOffRequestApprovalFlow.forcedBy',
            'timeOffRequestApprovalFlow.timeOffComment',
            'timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps.timeOffApprovalFlowStep.timeOffApproverType',
            'timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps.timeOffRequestApprovalFlow',
            'timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps.timeOffRequestApprovals.approver',
            'timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps.timeOffRequestApprovals.timeOffComment',
            'timeOffRequestApprovalFlow.timeOffRequestApprovalFlowStepsWithTrashed',
        ]).find(this.timeOffRequest.id);

        await this.updatePermissions();
    }

    async onDelete(): Promise<void> {
        if (
            !this.isAdmin &&
            !this.isManager &&
            this.timeOffRequest.status === TimeOffRequestStatus.approved &&
            moment(this.timeOffRequest.startAt).isSameOrBefore(moment(), 'day')
        ) {
            this.isUnableToDeleteModalOpen = true;
            return;
        }

        const approved = await this.deleteRequestModal.open();

        if (!approved) {
            return;
        }

        this.setIsLoading(true);

        try {
            await this.timeOffRequest.delete();
            this.notify.success('time-off-v3.timeOffRequestHasBeenDeletedSuccessfully');
            this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestMeatBallDelete);

            if (!this.routingState.landedOnPage && !this.routingState.previousUrl.includes('edit')) {
                this.location.back();
                return;
            }

            this.router.navigate(['/', 'time-off', 'v3']);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.setIsLoading(false);
        }
    }

    async onReset(): Promise<void> {
        const approved = await this.resetRequestModal.open();

        if (!approved) {
            return;
        }

        this.setIsLoading(true);
        await this.timeOffService.resetTimeOffRequest(this.timeOffRequest);
        await this.reloadTimeOffRequest();
        this.setIsLoading(false);
        await this.notify.success('time-off-v3.timeOffRequestHasBeenResetSuccessfully');
        this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestMeatBallReset);
    }

    async onForceApprove(): Promise<void> {
        const approved = await this.forceApproveModal.open();

        if (!approved) {
            return;
        }

        this.setIsLoading(true);
        await this.timeOffService.forceApproveTimeOffRequest(this.timeOffRequest);
        await this.reloadTimeOffRequest();
        this.setIsLoading(false);
        await this.notify.success('time-off-v3.timeOffRequestHasBeenForceApprovedSuccessfully');
        this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestMeatBallForceApprove);
    }

    onEdit(): void {
        if (
            !this.isAdmin &&
            !this.isManager &&
            this.timeOffRequest.status === TimeOffRequestStatus.approved &&
            moment(this.timeOffRequest.startAt).isSameOrBefore(moment(), 'day')
        ) {
            this.isUnableToEditModalOpen = true;

            return;
        }

        this.router.navigate(['/', 'time-off', 'v3', 'requests', this.timeOffRequest.id, 'edit']);
    }

    onCloseForceDenyRequestModal(): void {
        this.isForceDenyRequestCommentModalOpen = false;
        return;
    }

    async onConfirmForceDenyRequest(comment?: string): Promise<void> {
        this.setIsLoading(true);

        try {
            await this.timeOffService.forceDenyTimeOffRequest(this.timeOffRequest, comment);
            await this.reloadTimeOffRequest();
            await this.notify.success('time-off-v3.timeOffRequestHasBeenForceDeniedSuccessfully');
            if (comment) {
                this.analyticService.trackEvent(
                    AnalyticEvents.TimeOffManageRequestMeatballForceDenyAddCommentClickForceDenyRequestToSave
                );
            }

            this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestMeatBallForceDeny);
        } catch (e) {
            this.notify.error(e.message);
        }

        this.isForceDenyRequestCommentModalOpen = false;
        this.setIsLoading(false);
    }

    async onForceDeny(): Promise<void> {
        this.isForceDenyRequestCommentModalOpen = true;
    }

    showDelete(): boolean {
        const timeOffRequestHasNotOccured = moment().isSameOrBefore(
            moment(this.timeOffRequest.startAt, TimeOffDateFormatDashes),
            'date'
        );

        if (this.isAdmin) {
            return true;
        }

        if (this.isManager) {
            /**
             * 3 cases for deletion:
             * 1. Date has occurred for request & request is not made by manager => allow deletion by manager
             * 2. Date has not occurred for request & manager is requested day off => allow deletion by manager
             * 3. Date has occurred for request & manager is not reqeuster => allow deletion by manager
             */
            return (
                (!timeOffRequestHasNotOccured && !this.isRequester) ||
                (timeOffRequestHasNotOccured && this.isRequester) ||
                (timeOffRequestHasNotOccured && !this.isRequester)
            );
        }

        return timeOffRequestHasNotOccured && this.isRequester;
    }

    closeUnableToDeleteModal(): void {
        this.isUnableToDeleteModalOpen = false;
    }

    closeUnableToEditModal(): void {
        this.isUnableToEditModalOpen = false;
    }

    private setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }

    private canEditCheck(): boolean {
        return !(this.timeOffRequest.status === TimeOffRequestStatus.denied);
    }

    private canResetCheck(): boolean {
        if (this.timeOffRequest.status === TimeOffRequestStatus.denied && this.isAdmin) {
            return false;
        }

        if (
            this.timeOffRequest.status === TimeOffRequestStatus.approved &&
            this.timeOffRequest.timeOffRequestApprovalFlow.isAutomatic
        ) {
            return false;
        }

        return this.isAdmin;
    }

    private canForceApproveCheck(): boolean {
        if (this.timeOffRequest.status === TimeOffRequestStatus.denied && this.isAdmin && !this.isForcedApproved) {
            return false;
        }

        if (this.timeOffRequest.status === TimeOffRequestStatus.approved) {
            return false;
        }

        return this.isAdmin && !this.isForcedApproved;
    }

    private canForceDenyCheck(): boolean {
        if (this.timeOffRequest.status === TimeOffRequestStatus.denied && this.isAdmin && !this.isForcedDenied) {
            return false;
        }

        if (this.timeOffRequest.status === TimeOffRequestStatus.denied) {
            return false;
        }

        return this.isAdmin && !this.isForcedDenied;
    }
}
